import {
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonLoading,
  IonPage,
  IonText,
  IonTitle,
  IonToolbar,
  IonSearchbar,
  useIonViewWillEnter,
  IonFooter,
  IonButton,
  useIonAlert,
  useIonViewDidLeave,
  IonIcon,
} from "@ionic/react";
import { chevronBackOutline, chevronBackSharp } from "ionicons/icons";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useParams, useHistory} from "react-router";
import InspectionGroupList from "../components/InspectionGroupList";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import type { LinkedInspection } from "../api/WorkOrders";
import { useEquipmentStore } from "../store/equipment";
import { useShallow } from "zustand/react/shallow";
import useGetLinkedEquipmentInspections from "../hooks/equipment/useGetLinkedEquipmentInspections";
import { useOfflineStore } from "../store/offline-mode";
import useDownloadEquipmentInspections from "../hooks/equipment/useDownloadEquipmentInspections";

type InspectionItem = {
  id: string;
  name: string;
  required: boolean;
  group: "Pre Job" | "Inspection" | "Post Job";
  columns: any[];
  inspectionId: number;
  plant: string;
  inspectionType: string;
};

const EquipmentInspections: React.FC = () => {
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const displayEquipment = decodeParam(equipmentId);
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [errorState, setErrorState] = useState("");
  const isFetchingRef = useRef(false);
  const [showLoader, setShowLoader] = useState(false);

  const { getLinkedInspections } = useGetLinkedEquipmentInspections();
  const inspections = useEquipmentStore(
    useShallow((state) => state.getInspectionsForEquipment(displayEquipment))
  );
  const { trigger } = useDownloadEquipmentInspections();
  const setDownloadedInspections = useEquipmentStore(
    (state) => state.setDownloadedInspections
  );
  const isOffline = useOfflineStore((state) => state.isOffline);

  const resetSelectedInspections = useEquipmentStore(
    (state) => state.resetSelectedInspections
  );
  const selectedInspection = useEquipmentStore(
    useShallow((state) => state.selectedInspection)
  );

  const [presentAlert] = useIonAlert();

  const processInspections = useCallback((inspections: LinkedInspection[]) => {
    const result: InspectionItem[] = [];
    inspections.forEach((inspection) => {
      const inspectionId = inspection.InspectionId;
      const required = inspection.Required;

      const description = inspection.Description;
      const plant = inspection.Plant;
      const inspectionType = inspection.InspectionType;

      const baseItem: InspectionItem = {
        id: inspectionId.toString(),
        name: description,
        required,
        columns: [],
        inspectionId,
        plant,
        inspectionType,
        group: "Inspection",
      };

      if (inspection.PreInspection && inspection.PostInspection) {
        result.push({ ...baseItem, group: "Pre Job" });
        result.push({ ...baseItem, group: "Post Job" });
      } else if (inspection.PreInspection) {
        result.push({ ...baseItem, group: "Pre Job" });
      } else if (inspection.PostInspection) {
        result.push({ ...baseItem, group: "Post Job" });
      } else {
        result.push(baseItem);
      }
    });
    return result;
  }, []);

  const { jobs, postJobs, preJobs } = useMemo(() => {
    let preJobs: InspectionItem[] = [];
    let jobs: InspectionItem[] = [];
    let postJobs: InspectionItem[] = [];
    if (inspections?.length) {
      const result = processInspections(inspections);
      preJobs = result.filter((r) => r.group === "Pre Job");
      jobs = result.filter((r) => r.group === "Inspection");
      postJobs = result.filter((r) => r.group === "Post Job");
    }

    return { preJobs, postJobs, jobs };
  }, [inspections, processInspections]);

  const fetchInspections = useCallback(async () => {
    if (!displayEquipment || isFetchingRef.current) {
      return;
    }
    if (inspections && typeof navigator !== "undefined" && !navigator.onLine) {
      return;
    }

    isFetchingRef.current = true;
    setShowLoading(true);
    setErrorState("");
    try {
      const response = await getLinkedInspections(displayEquipment);

      if (response.isError) {
        setErrorState(
          translatedMessages["ErrLoadingInspections"]?.MessageText ||
            translations["err_loading_inspections"] ||
            "Failed to load inspections"
        );
      }
    } catch (e) {
      console.error("Error loading equipment inspections:", e);
      setErrorState(
        translatedMessages["ErrLoadingInspections"]?.MessageText ||
          translations["err_loading_inspections"] ||
          "Failed to load inspections"
      );
    } finally {
      isFetchingRef.current = false;
      setShowLoading(false);
    }
  }, [
    displayEquipment,
    getLinkedInspections,
    inspections,
    translatedMessages,
    translations,
  ]);

  useIonViewWillEnter(() => {
    fetchInspections();
  });

  useIonViewDidLeave(() => {
    resetSelectedInspections();
  });

  const matchesSearch = useCallback(
    (item: InspectionItem) => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return true;
      return (
        item.id.toLowerCase().includes(term) ||
        item.name.toLowerCase().includes(term) ||
        item.inspectionType.toLowerCase().includes(term)
      );
    },
    [searchTerm]
  );

  const filteredPreJobs = preJobs.filter(matchesSearch);
  const filteredJobs = jobs.filter(matchesSearch);
  const filteredPostJobs = postJobs.filter(matchesSearch);

  const hasInspections =
    filteredPreJobs.length > 0 ||
    filteredJobs.length > 0 ||
    filteredPostJobs.length > 0;

  const handleDownloadEquipForOfflineAccess = useCallback(async () => {
    if (!selectedInspection?.length) return;
    setShowLoader(true);
    await trigger();
    setDownloadedInspections(selectedInspection);
    resetSelectedInspections();
    setShowLoader(false);
    presentAlert({
      message:
        translatedMessages["MobOfflineInspectionDownloadComplete"]
          ?.MessageText ||
        "Equipment Inspections downloaded for offline access.",
      buttons: ["OK"],
    });
  }, [
    presentAlert,
    resetSelectedInspections,
    selectedInspection,

    setDownloadedInspections,
    translatedMessages,
    trigger,
  ]);

  return (
    <>
      <IonLoading
        isOpen={showLoader}
        onDidDismiss={() => setShowLoader(false)}
      />
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
            <IonButton
              onClick={() => {
                history.push(`/Equipment/${encodeParam(displayEquipment)}`);
              }}
            >
              <IonIcon
                slot="icon-only"
                ios={chevronBackOutline}
                md={chevronBackSharp}
              />
            </IonButton>
          </IonButtons>
            <IonTitle>
              {displayEquipment} -{" "}
              {translations["lbl_inspections"] || "Inspections"}
            </IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          <IonSearchbar
            value={searchTerm}
            onIonChange={(e) => setSearchTerm(e.detail.value ?? "")}
            debounce={300}
            placeholder={
              translations["lbl_search_inspections"] || "Search inspections"
            }
          />

          {errorState && (
            <IonItem lines="none">
              <IonLabel>
                <IonText color="danger">{errorState}</IonText>
              </IonLabel>
            </IonItem>
          )}

          {!showLoading && hasInspections && !errorState && (
            <>
              {filteredPreJobs.length > 0 && (
                <InspectionGroupList
                  title={
                    translations["lbl_pre_job_inspections"] ||
                    "Pre Job Inspections"
                  }
                  inspections={filteredPreJobs}
                  isEquipmentContext
                  equipmentId={displayEquipment}
                  isSelectable
                />
              )}
              {filteredJobs.length > 0 && (
                <InspectionGroupList
                  title={translations["lbl_inspections"] || "Inspections"}
                  inspections={filteredJobs}
                  isEquipmentContext
                  equipmentId={displayEquipment}
                  isSelectable
                />
              )}
              {filteredPostJobs.length > 0 && (
                <InspectionGroupList
                  title={
                    translations["lbl_post_job_inspections"] ||
                    "Post Job Inspections"
                  }
                  inspections={filteredPostJobs}
                  isEquipmentContext
                  equipmentId={displayEquipment}
                  isSelectable
                />
              )}
            </>
          )}

          {!showLoading && !hasInspections && !errorState && (
            <IonItem lines="none">
              <IonLabel>
                <IonText>
                  {translatedMessages["NoInspectionsExist"]?.MessageText ||
                    translations["msg_no_inspections_exist"] ||
                    "No inspections exist"}
                </IonText>
              </IonLabel>
            </IonItem>
          )}

          <IonLoading
            cssClass="my-custom-class"
            isOpen={showLoading}
            onDidDismiss={() => setShowLoading(false)}
          />
        </IonContent>

        <IonFooter translucent={true} hidden={!isOffline}>
          <IonToolbar>
            <IonButtons slot="end">
              <IonButton
                disabled={!selectedInspection?.length}
                onClick={handleDownloadEquipForOfflineAccess}
                fill="solid"
              >
                {translations["mob_lbl_download"] || "Download"}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonPage>
    </>
  );
};

export default EquipmentInspections;
